class OzProgressiveProductRecommendations extends HTMLElement {
  #observer;
  #resizeObserver;
  #controllers = new Set();
  #cache = new Map();
  #seenIds = new Set();
  #started = false;
  #scrollFrames = new WeakMap();

  connectedCallback() {
    this.#syncState();

    this.#observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.#observer?.disconnect();
        this.#start();
      },
      { rootMargin: '0px 0px 600px 0px' }
    );

    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#resizeObserver?.disconnect();
    for (const controller of this.#controllers) controller.abort();
    this.#controllers.clear();
  }

  async #start() {
    if (this.#started) return;
    this.#started = true;
    this.setAttribute('aria-busy', 'true');

    const pending = this.#pendingGrids();
    if (pending.length) {
      await Promise.allSettled(pending.map((grid) => this.#loadNativeGrid(grid)));
    }

    this.#deduplicateAll();
    this.#setupSliders();
    await this.#seedUnscrollableSliders();
    this.#syncState();
    this.dataset.ozReady = 'true';
    this.setAttribute('aria-busy', 'false');
  }

  #pendingGrids() {
    return [...this.querySelectorAll('[data-oz-rec-grid][data-loaded="false"]')];
  }

  #withPreviewParameters(url) {
    const current = new URL(window.location.href);
    for (const key of ['preview_theme_id', '_fd', 'pb']) {
      const value = current.searchParams.get(key);
      if (value && !url.searchParams.has(key)) url.searchParams.set(key, value);
    }
    return url;
  }

  async #fetchText(url, cacheKey = url.href) {
    if (this.#cache.has(cacheKey)) return this.#cache.get(cacheKey);

    const controller = new AbortController();
    this.#controllers.add(controller);
    try {
      const response = await fetch(url.href, {
        signal: controller.signal,
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });
      if (!response.ok) throw new Error(`Recommendation request failed: ${response.status}`);
      const html = await response.text();
      this.#cache.set(cacheKey, html);
      return html;
    } finally {
      this.#controllers.delete(controller);
    }
  }

  async #loadNativeGrid(grid) {
    const intent = grid.dataset.ozRecGrid;
    const { productId, sectionId, url } = this.dataset;

    if (!intent || !productId || !sectionId || !url) {
      this.#markNativeGridComplete(grid);
      return;
    }

    const requestUrl = this.#withPreviewParameters(new URL(url, window.location.origin));
    requestUrl.searchParams.set('product_id', productId);
    requestUrl.searchParams.set('section_id', sectionId);
    requestUrl.searchParams.set('intent', intent);

    try {
      const html = await this.#fetchText(requestUrl, requestUrl.href);
      const documentFragment = new DOMParser().parseFromString(html, 'text/html');
      const replacement = documentFragment.querySelector(`[data-oz-rec-grid="${intent}"]`);
      if (!replacement) {
        this.#markNativeGridComplete(grid);
        return;
      }
      grid.replaceWith(replacement);
    } catch (error) {
      if (error?.name !== 'AbortError') this.#markNativeGridComplete(grid);
    }
  }

  #markNativeGridComplete(grid) {
    grid.dataset.loaded = 'true';
    if (!this.#poolHandles(grid).length && !grid.querySelector('[data-oz-rec-product-id]')) {
      grid.hidden = true;
    }
  }

  #poolHandles(grid) {
    return String(grid.dataset.ozPools || '')
      .split('|')
      .map((value) => value.trim())
      .filter(Boolean);
  }

  #deduplicateAll() {
    this.#seenIds = new Set([String(this.dataset.productId || '')]);
    for (const excluded of String(this.dataset.excludedIds || '').split('|')) {
      if (excluded) this.#seenIds.add(excluded);
    }

    for (const intent of ['complementary', 'related']) {
      const grid = this.querySelector(`[data-oz-rec-grid="${intent}"]`);
      if (!grid) continue;
      for (const item of [...grid.querySelectorAll('[data-oz-rec-product-id]')]) {
        const id = String(item.dataset.ozRecProductId || '');
        if (!id || this.#seenIds.has(id)) {
          item.remove();
          continue;
        }
        this.#seenIds.add(id);
      }
    }
  }

  #setupSliders() {
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const grid = entry.target.closest('[data-oz-rec-grid]');
        if (grid) this.#updateControls(grid);
      }
    });

    for (const grid of this.querySelectorAll('[data-oz-rec-grid]')) {
      const list = grid.querySelector('[data-oz-rec-list]');
      const previous = grid.querySelector('[data-oz-slider-prev]');
      const next = grid.querySelector('[data-oz-slider-next]');
      if (!list || !previous || !next) continue;

      if (grid.dataset.ozSliderReady !== 'true') {
        grid.dataset.ozSliderReady = 'true';

        previous.addEventListener('click', () => {
          list.scrollBy({ left: -this.#scrollAmount(list), behavior: this.#scrollBehavior() });
        });

        next.addEventListener('click', async () => {
          if (this.#isNearEnd(list)) await this.#loadMore(grid);
          list.scrollBy({ left: this.#scrollAmount(list), behavior: this.#scrollBehavior() });
        });

        list.addEventListener(
          'scroll',
          () => {
            if (this.#scrollFrames.has(list)) return;
            const frame = requestAnimationFrame(async () => {
              this.#scrollFrames.delete(list);
              this.#updateControls(grid);
              if (this.#isNearEnd(list)) await this.#loadMore(grid);
            });
            this.#scrollFrames.set(list, frame);
          },
          { passive: true }
        );
      }

      this.#resizeObserver.observe(list);
      this.#updateControls(grid);
    }
  }

  #scrollBehavior() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  }

  #scrollAmount(list) {
    return Math.max(240, Math.round(list.clientWidth * 0.88));
  }

  #isNearEnd(list) {
    const remaining = list.scrollWidth - list.clientWidth - list.scrollLeft;
    return remaining <= Math.max(220, list.clientWidth * 1.1);
  }

  #updateControls(grid) {
    const list = grid.querySelector('[data-oz-rec-list]');
    const previous = grid.querySelector('[data-oz-slider-prev]');
    const next = grid.querySelector('[data-oz-slider-next]');
    if (!list || !previous || !next) return;

    const canScroll = list.scrollWidth > list.clientWidth + 4;
    previous.disabled = !canScroll || list.scrollLeft <= 4;
    const atVisualEnd = list.scrollLeft + list.clientWidth >= list.scrollWidth - 4;
    const canLoad = grid.dataset.ozExhausted !== 'true' && this.#itemCount(grid) < this.#maxProducts();
    next.disabled = !canScroll && !canLoad ? true : atVisualEnd && !canLoad;
  }

  async #seedUnscrollableSliders() {
    const grids = [...this.querySelectorAll('[data-oz-rec-grid]')];
    await Promise.allSettled(
      grids.map(async (grid) => {
        const list = grid.querySelector('[data-oz-rec-list]');
        if (!list || grid.dataset.ozExhausted === 'true') return;
        if (!list.querySelector('[data-oz-rec-product-id]') || list.scrollWidth <= list.clientWidth + 4) {
          await this.#loadMore(grid);
        }
      })
    );
  }

  #maxProducts() {
    return Math.max(8, Number.parseInt(this.dataset.maxProductsTotal || '24', 10) || 24);
  }

  #batchSize() {
    return Math.max(2, Number.parseInt(this.dataset.loadBatchSize || '4', 10) || 4);
  }

  #itemCount(grid) {
    return grid.querySelectorAll('[data-oz-rec-product-id]').length;
  }

  #collectionFeedUrl(handle, page) {
    const root = String(window.Shopify?.routes?.root || '/');
    const normalisedRoot = root.endsWith('/') ? root : `${root}/`;
    const url = new URL(`${normalisedRoot}collections/${encodeURIComponent(handle)}`, window.location.origin);
    url.searchParams.set('view', 'oz-recommendation-feed');
    url.searchParams.set('page', String(page));
    return this.#withPreviewParameters(url);
  }

  async #loadMore(grid) {
    if (grid.dataset.ozLoading === 'true' || grid.dataset.ozExhausted === 'true') return;
    if (this.#itemCount(grid) >= this.#maxProducts()) {
      grid.dataset.ozExhausted = 'true';
      this.#updateControls(grid);
      return;
    }

    const pools = this.#poolHandles(grid);
    if (!pools.length) {
      grid.dataset.ozExhausted = 'true';
      this.#updateControls(grid);
      return;
    }

    grid.dataset.ozLoading = 'true';
    const loading = grid.querySelector('[data-oz-rec-loading]');
    const status = grid.querySelector('[data-oz-rec-status]');
    if (loading) loading.hidden = false;

    const list = grid.querySelector('[data-oz-rec-list]');
    const batchTarget = Math.min(this.#batchSize(), this.#maxProducts() - this.#itemCount(grid));
    let appended = 0;
    let networkPages = 0;

    try {
      while (appended < batchTarget && networkPages < 4 && grid.dataset.ozExhausted !== 'true') {
        let poolIndex = Number.parseInt(grid.dataset.ozPoolIndex || '0', 10) || 0;
        let page = Number.parseInt(grid.dataset.ozNextPage || '1', 10) || 1;

        if (poolIndex >= pools.length) {
          grid.dataset.ozExhausted = 'true';
          break;
        }

        const handle = pools[poolIndex];
        const url = this.#collectionFeedUrl(handle, page);
        const html = await this.#fetchText(url, url.href);
        networkPages += 1;

        const documentFragment = new DOMParser().parseFromString(html, 'text/html');
        const feed = documentFragment.querySelector('[data-oz-recommendation-feed]');
        const totalPages = Math.max(1, Number.parseInt(feed?.dataset.pages || '1', 10) || 1);
        const candidates = feed ? [...feed.querySelectorAll('[data-oz-feed-candidate]')] : [];

        for (const candidate of candidates) {
          if (appended >= batchTarget || this.#itemCount(grid) >= this.#maxProducts()) break;
          if (!this.#isEligible(candidate, grid)) continue;

          const id = String(candidate.dataset.ozRecProductId || '');
          this.#seenIds.add(id);
          candidate.removeAttribute('data-oz-feed-candidate');
          list.append(candidate);
          appended += 1;
        }

        if (page >= totalPages || !feed) {
          poolIndex += 1;
          page = 1;
        } else {
          page += 1;
        }

        grid.dataset.ozPoolIndex = String(poolIndex);
        grid.dataset.ozNextPage = String(page);
        if (poolIndex >= pools.length) grid.dataset.ozExhausted = 'true';
      }

      if (this.#itemCount(grid) >= this.#maxProducts()) grid.dataset.ozExhausted = 'true';
      if (appended > 0) {
        grid.hidden = false;
        if (status) status.textContent = `${appended} more relevant product${appended === 1 ? '' : 's'} loaded.`;
      } else if (grid.dataset.ozExhausted === 'true' && this.#itemCount(grid) === 0) {
        grid.hidden = true;
      }
    } catch (error) {
      if (error?.name !== 'AbortError' && status) status.textContent = 'More products could not be loaded right now.';
    } finally {
      grid.dataset.ozLoading = 'false';
      if (loading) loading.hidden = true;
      this.#updateControls(grid);
      this.#syncState();
    }
  }

  #isEligible(candidate, grid) {
    const id = String(candidate.dataset.ozRecProductId || '');
    if (!id || this.#seenIds.has(id)) return false;

    const intent = String(grid.dataset.ozRecGrid || '');
    const currentType = String(this.dataset.currentType || '').trim().toLowerCase();
    const currentLeaf = String(this.dataset.currentLeaf || '').trim().toLowerCase();
    const currentSize = String(this.dataset.currentSize || '').trim().toLowerCase();
    const candidateType = String(candidate.dataset.candidateType || '').trim().toLowerCase();
    const candidateLeaf = String(candidate.dataset.candidateLeaf || '').trim().toLowerCase();
    const candidateTitle = String(candidate.dataset.candidateTitle || '').trim().toLowerCase();
    const candidateSize = String(candidate.dataset.ozRecSizeToken || '').trim().toLowerCase();

    const currentFamily = this.#sizeFamily(currentSize);
    const candidateFamily = this.#sizeFamily(candidateSize);
    if (currentSize && candidateSize && currentFamily === candidateFamily && currentSize !== candidateSize) {
      return false;
    }

    if (intent === 'related') {
      const exactType = currentType && candidateType && currentType === candidateType;
      const exactLeaf = currentLeaf && candidateLeaf && currentLeaf === candidateLeaf;
      if (!exactType && !exactLeaf) return false;
      if (currentSize && ['bed', 'seats'].includes(currentFamily) && !candidateSize) return false;
      candidate.dataset.ozRecScore = exactType ? '94' : '88';
      return true;
    }

    if (currentType && candidateType && currentType === candidateType) return false;

    const needsSizeMatch = grid.dataset.ozNeedsSizeMatch === 'true';
    if (needsSizeMatch && currentSize) {
      if (candidateSize && candidateSize !== currentSize) return false;
      if (!candidateSize && ['mattress', 'protector', 'bed frame'].some((term) => candidateTitle.includes(term))) {
        return false;
      }
    }

    const requiredTerms = String(grid.dataset.ozRequiredTerms || '')
      .split('|')
      .map((term) => term.trim().toLowerCase())
      .filter(Boolean);
    if (requiredTerms.length && !requiredTerms.some((term) => candidateTitle.includes(term))) return false;

    candidate.dataset.ozRecScore = '88';
    return true;
  }

  #sizeFamily(token) {
    return token ? token.split('-')[0] : '';
  }

  #syncState() {
    const grids = [...this.querySelectorAll('[data-oz-rec-grid]')];
    const hasVisibleProducts = grids.some(
      (grid) => !grid.hidden && grid.querySelector('[data-oz-rec-product-id]')
    );
    const stillPending = grids.some((grid) => grid.dataset.loaded === 'false');
    const canStillLoad = grids.some((grid) => grid.dataset.ozExhausted !== 'true');
    this.hidden = !hasVisibleProducts && !stillPending && !canStillLoad;
  }
}

if (!customElements.get('oz-product-recommendations')) {
  customElements.define('oz-product-recommendations', OzProgressiveProductRecommendations);
}
